import React, { ReactNode } from 'react';
import { InfoWindow as GoogleInfoWindow } from '@vis.gl/react-google-maps';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';

export interface IInfoWindowAction {
	label: string;
	onClick: () => void;
	className?: string;
	icon?: string;
}

export interface IInfoWindowContact {
	type: 'phone' | 'mobile' | 'email';
	label: string;
	value: string;
	href?: string;
}

export interface IInfoWindowData {
	title: string;
	subtitle?: string;
	address?: {
		street?: string;
		city?: string;
		postalCode?: string;
	};
	contacts?: IInfoWindowContact[];
	customContent?: ReactNode;
	actions?: IInfoWindowAction[];
}

interface IStandardInfoWindow {
	position: { lat: number; lng: number };
	onClose: () => void;
	data: IInfoWindowData;
	headerContent?: ReactNode;
}

export const StandardInfoWindow: React.FC<IStandardInfoWindow> = ({ position, onClose, data, headerContent }) => {
	const defaultHeaderContent = <strong>{data.title}</strong>;

	const renderContact = (contact: IInfoWindowContact) => {
		const href = contact.href || (contact.type === 'email' ? `mailto:${contact.value}` : contact.type === 'phone' || contact.type === 'mobile' ? `tel:${contact.value}` : '');

		return (
			<tr key={contact.type}>
				<td>{contact.label}</td>
				<td className="has-text-left">
					{href ? (
						<a href={href} className="button is-text is-padded">
							{contact.value}
						</a>
					) : (
						<span>{contact.value}</span>
					)}
				</td>
			</tr>
		);
	};

	return (
		<GoogleInfoWindow position={position} onClose={onClose} headerContent={headerContent || defaultHeaderContent}>
			<div className="info-window-content">
				{(data.subtitle || data.address || data.contacts) && (
					<table className="table is-text">
						<tbody>
							{data.subtitle && (
								<tr>
									<td className="full-width" colSpan={2}>
										<strong>{data.subtitle}</strong>
									</td>
								</tr>
							)}
							{data.address && (
								<tr>
									<td colSpan={2}>
										{data.address.street && <div>{data.address.street}</div>}
										{(data.address.postalCode || data.address.city) && (
											<div>
												{data.address.postalCode} {data.address.city}
											</div>
										)}
									</td>
								</tr>
							)}
							{data.contacts?.map(renderContact)}
						</tbody>
					</table>
				)}

				{data.customContent && <div className="custom-content">{data.customContent}</div>}

				{data.actions && data.actions.length > 0 && (
					<div className="info-window-actions">
						{data.actions.map((action, index) => (
							<Button key={index} className={action.className || 'button is-small is-turquoise is-inverted'} onClick={action.onClick}>
								{action.icon && <Icon iconClass={action.icon} faModifier="fas" />}
								<span>{action.label}</span>
							</Button>
						))}
					</div>
				)}
			</div>
		</GoogleInfoWindow>
	);
};

// Specialized InfoWindow for Agency data
export interface IAgenturInfoWindow {
	position: { lat: number; lng: number };
	onClose: () => void;
	agt: {
		name: string;
		agtId: number;
		gebiet: { vgbStufe2Text: string };
		vtnrStr?: string;
		vtnrPlz?: number | string;
		vtnrOrt?: string;
		vtnrTelFest?: string;
		vtnrTelMobil?: string;
		email?: string;
	};
	onViewDetails?: (agtId: number) => void;
}

export const AgenturInfoWindow: React.FC<IAgenturInfoWindow> = ({ position, onClose, agt, onViewDetails }) => {
	const data: IInfoWindowData = {
		title: agt.name,
		subtitle: agt.gebiet.vgbStufe2Text,
		address: {
			street: agt.vtnrStr,
			postalCode: agt.vtnrPlz?.toString(),
			city: agt.vtnrOrt,
		},
		contacts: [
			...(agt.vtnrTelFest
				? [
						{
							type: 'phone' as const,
							label: 'Telefon',
							value: agt.vtnrTelFest,
						},
				  ]
				: []),
			...(agt.vtnrTelMobil
				? [
						{
							type: 'mobile' as const,
							label: 'Mobil',
							value: agt.vtnrTelMobil,
						},
				  ]
				: []),
			...(agt.email
				? [
						{
							type: 'email' as const,
							label: 'E-Mail',
							value: agt.email,
						},
				  ]
				: []),
		],
		actions: onViewDetails
			? [
					{
						label: 'Detailansicht',
						onClick: () => onViewDetails(agt.agtId),
						className: 'button is-small is-turquoise is-inverted',
					},
			  ]
			: [],
	};

	return <StandardInfoWindow position={position} onClose={onClose} data={data} />;
};

// Specialized InfoWindow for LeadNow data
export interface ILeadNowInfoWindow {
	position: { lat: number; lng: number };
	onClose: () => void;
	items: Array<{
		agt: { name: string; agtId: number };
		netto: number;
		brutto: number;
	}>;
	title?: string;
}

export const LeadNowInfoWindow: React.FC<ILeadNowInfoWindow> = ({ position, onClose, items, title = 'AMIS.NOW Leads' }) => {
	const customContent = (
		<table className="table is-compact">
			<thead>
				<tr>
					<th>Agentur</th>
					<th>Netto</th>
					<th>Brutto</th>
				</tr>
			</thead>
			<tbody>
				{items.map((item) => (
					<tr key={'ldnt' + item.agt.agtId}>
						<td>
							<strong>{item.agt.name}</strong>
						</td>
						<td>{item.netto}</td>
						<td>{item.brutto}</td>
					</tr>
				))}
			</tbody>
		</table>
	);

	const data: IInfoWindowData = {
		title,
		customContent,
	};

	return <StandardInfoWindow position={position} onClose={onClose} data={data} />;
};
